import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  alpha,
  useTheme,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router-dom";
import { useGetList, useGetOne } from "react-admin";
import MUIButton from "@/components/ui/Button";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { getCarrierLabel } from "@/components/common/PolicyField";
import { ClaimItem, Policy, ResourceName, StoplossClaim } from "@/types";
import { ButtonStyleType, ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor } from "@/utils/helpers/getStatusColor";

/** A claim line in the document under review that will insert on approve. */
export interface IncomingClaimLine {
  serviceDate?: string;
  paidDate?: string;
  procedureCode?: string;
  procedureDescription?: string;
  paidAmount: number;
}

interface StoplossClaimPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** The claim to preview; null renders nothing. */
  stoplossClaimId: string | null;
  /** Shown in the header when the caller already knows it (the review grid does). */
  claimantName?: string;
  /**
   * What approving the document under review does to this claim, from the
   * review projection: the claimant's new paid spend this file adds and the
   * deductible the claim is measured against. The loader nets prior
   * submissions on other claims at approve, so the "after" figure is
   * labelled before netting.
   */
  onApprove?: { adds: number; deductible: number };
  /** The document's claim lines for this claimant that will insert on approve — shown as incoming rows. */
  incomingLines?: IncomingClaimLine[];
}

/** Items on this page the claim amount can count: eligible and paid. */
const countsTowardClaim = (item: ClaimItem): boolean => item.is_eligible !== false && item.claim_status === "paid";

const LOAD_FAILED_NOTE = "Couldn't load this claim — use Open claim for the full page.";

/** One row of the lines table: a claim item on file, or an incoming line from the document. */
type PreviewLine = {
  id: string;
  incoming: boolean;
  serviceDate?: string | null;
  paidDate?: string | null;
  procedure: string;
  paidAmount: number | null;
  status: ClaimItem["claim_status"] | null;
  ineligible: boolean;
  ineligibleReasons?: string[];
};

const toPreviewLine = (item: ClaimItem): PreviewLine => ({
  id: String(item.id),
  incoming: false,
  serviceDate: item.date_of_service_start ?? item.date_of_service,
  paidDate: item.paid_date,
  procedure: [item.procedure_code, item.procedure_description].filter(Boolean).join(" ") || item.provider || "—",
  paidAmount: typeof item.paid_amount === "number" ? item.paid_amount : null,
  status: item.claim_status ?? null,
  ineligible: item.is_eligible === false,
  ineligibleReasons: item.ineligible_reasons,
});

const incomingToPreviewLine = (line: IncomingClaimLine, i: number): PreviewLine => ({
  id: `incoming-${i}`,
  incoming: true,
  serviceDate: line.serviceDate,
  paidDate: line.paidDate,
  procedure: [line.procedureCode, line.procedureDescription].filter(Boolean).join(" ") || "—",
  paidAmount: line.paidAmount,
  status: null,
  ineligible: false,
});

/** "Summit Re 2026" — carrier + year, the policy display convention. */
const policyLabel = (policy: Policy | undefined): string | null => {
  if (!policy) return null;
  const a = policy.effective_start_date?.slice(0, 4);
  const b = policy.effective_end_date?.slice(0, 4);
  const years = !a ? null : b && b !== a ? `${a}–${b}` : a;
  return [getCarrierLabel(policy), years].filter(Boolean).join(" ");
};

const buildColumns = (incomingLabel: string): CompactTableColumn<PreviewLine>[] => [
  {
    key: "service",
    label: "Service date",
    width: "110px",
    render: (l) => (
      <Typography variant="caption" color="text.secondary">
        {formatLongDate(l.serviceDate)}
      </Typography>
    ),
  },
  {
    key: "paid_date",
    label: "Paid date",
    width: "110px",
    render: (l) => (
      <Typography variant="caption" color="text.secondary">
        {formatLongDate(l.paidDate)}
      </Typography>
    ),
  },
  {
    key: "procedure",
    label: "Procedure",
    width: "1fr",
    render: (l) => (
      <Typography variant="body2" noWrap title={l.procedure}>
        {l.procedure}
      </Typography>
    ),
  },
  {
    key: "paid_amount",
    label: "Paid",
    width: "100px",
    align: "right",
    render: (l) => (
      <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
        {l.paidAmount !== null ? formatUSD(l.paidAmount) : "—"}
      </Typography>
    ),
  },
  {
    key: "status",
    label: "Status",
    width: "110px",
    align: "right",
    render: (l) => (
      <Box
        sx={{ display: "flex", justifyContent: "flex-end" }}
        title={(l.ineligibleReasons ?? []).join("; ") || undefined}
      >
        {l.incoming ? (
          <Typography
            component="span"
            variant="caption"
            sx={{ fontWeight: 700, color: "secondary.main", letterSpacing: 0.3, textTransform: "uppercase" }}
          >
            {incomingLabel}
          </Typography>
        ) : (
          <StatusChip
            label={l.ineligible ? "Ineligible" : formatToTitleCase(l.status || "—")}
            color={l.ineligible ? ColorType.Warning : (getStatusColor(l.status ?? undefined) ?? ColorType.Default)}
            size={UIComponentSize.Small}
          />
        )}
      </Box>
    ),
  },
];

/**
 * A read-only look at an open stop-loss claim from inside the review
 * (walkthrough 2026-10-15): the "Adds to open SLC" pill used to open the
 * claim page in a new tab, which took the reviewer out of the review for a
 * question — "which claim, with what on it?" — that fits in a dialog.
 *
 * Strictly what a reviewer wants when a file adds to an existing claim: the
 * claim AMOUNT now → after approve (the arithmetic — amount = paid −
 * deductible, netting — is a hover; a reviewer compared the amount with the
 * lines once and assumed a bug), then the claim items on file with this
 * document's incoming lines on top, marked so the potential reads apart from
 * the current. Dates, submissions and notes are on the full page (Open claim).
 *
 * Fetches the same way the claim page does (useGetOne for the claim,
 * useGetList on claim-items filtered by stoploss_claim_id_ref) so the two can
 * never disagree about what is on the claim.
 */
export const StoplossClaimPreviewDialog = ({
  open,
  onClose,
  stoplossClaimId,
  claimantName,
  onApprove,
  incomingLines = [],
}: StoplossClaimPreviewDialogProps) => {
  const theme = useTheme();
  const navy = theme.palette.secondary.main;
  const enabled = open && !!stoplossClaimId;
  const {
    data: claim,
    isLoading: claimLoading,
    error: claimError,
  } = useGetOne<StoplossClaim>(ResourceName.STOPLOSS_CLAIMS, { id: stoplossClaimId ?? "" }, { enabled });
  const {
    data: items = [],
    total: itemsTotal,
    isLoading: itemsLoading,
    error: itemsError,
  } = useGetList<ClaimItem>(
    ResourceName.CLAIM_ITEMS,
    {
      filter: { stoploss_claim_id_ref: stoplossClaimId },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "paid_date", order: "DESC" },
    },
    { enabled }
  );
  const { data: policy } = useGetOne<Policy>(
    ResourceName.POLICIES,
    { id: claim?.policy_id_ref ?? "" },
    { enabled: enabled && !!claim?.policy_id_ref }
  );

  if (!stoplossClaimId) return null;

  // A failed fetch must not read as an empty claim: a documents:show viewer
  // (#7433 AC3) may lack stoploss_claims:show or claim_items:list, and a 403
  // rendering "0 claim items" is wrong information on a money preview.
  const loadFailed = !!claimError || !!itemsError;
  // The hover's arithmetic is shown only when it adds up: eligible, paid
  // lines on this page, all of the claim's items on the page, and the sum
  // minus the deductible equal to the amount the claim carries. Otherwise
  // the generic sentence — a hover that does not reconcile is the confusion
  // it was added to explain (!4939 review).
  const itemCount = typeof itemsTotal === "number" ? itemsTotal : items.length;
  const allItemsLoaded = !itemsLoading && !itemsError && itemCount <= items.length;
  const linesPaid = allItemsLoaded
    ? items
        .filter(countsTowardClaim)
        .reduce((sum, it) => sum + (typeof it.paid_amount === "number" ? it.paid_amount : 0), 0)
    : null;
  const adds = onApprove && onApprove.adds > 0 ? onApprove.adds : 0;
  const amountNow = typeof claim?.total_paid_amount === "number" ? claim.total_paid_amount : null;
  const amountAfter = amountNow !== null && adds > 0 ? amountNow + adds : null;
  const deductible = onApprove?.deductible ?? 0;
  const arithmeticReconciles =
    linesPaid !== null && amountNow !== null && deductible > 0 && Math.abs(linesPaid - deductible - amountNow) < 0.005;
  const columns = buildColumns("On approve");
  const rows: PreviewLine[] = [...incomingLines.map(incomingToPreviewLine), ...items.map(toPreviewLine)];
  const incomingSuffix = incomingLines.length === 0 ? "" : ` · ${incomingLines.length} incoming on approve`;
  const itemsHeading = itemsLoading
    ? "Claim items"
    : `${itemCount} claim ${itemCount === 1 ? "item" : "items"}${incomingSuffix}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="slc-preview-title">
      <DialogTitle
        id="slc-preview-title"
        sx={{ pb: 0.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3 }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 600, fontSize: 22 }}>
            {/* The aegle_id rides as a hover on the title — it is on the pill's
                tooltip and the full page, and nobody reads it at a glance. */}
            <Box component="span" title={claim?.aegle_id}>
              {claimLoading ? "Loading claim…" : "Open SLC"}
            </Box>
            {claim?.claim_status && (
              <StatusChip
                label={formatToTitleCase(claim.claim_status)}
                color={getStatusColor(claim.claim_status) ?? ColorType.Default}
                size={UIComponentSize.Small}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            {[
              claimantName,
              policyLabel(policy),
              claim?.stoploss_claim_type ? formatToTitleCase(claim.stoploss_claim_type) : null,
            ]
              .filter(Boolean)
              .join(" · ") || "\u00a0"}
          </Typography>
        </Box>
        {/* The amount sits opposite the title, large, so it reads before the
            table does: now → after approve, the change in the primary colour.
            The arithmetic (amount = paid − deductible; netting) is a hover. */}
        {amountNow !== null && (
          <Tooltip
            arrow
            placement="bottom-end"
            title={
              arithmeticReconciles && linesPaid !== null
                ? `Now: ${formatUSD(linesPaid)} paid − ${formatUSD(deductible)} deductible.` +
                  (amountAfter !== null
                    ? ` On approve: ${formatUSD(linesPaid + adds)} paid − ${formatUSD(deductible)} deductible, before netting against other submissions.`
                    : "")
                : "The claim amount is the spend over the deductible."
            }
          >
            <Box
              data-testid="slc-preview-amount"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                flexShrink: 0,
                cursor: "default",
                px: 1.5,
                py: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                minWidth: 220,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  lineHeight: 1,
                  alignSelf: "stretch",
                  textAlign: "right",
                  pb: 0.75,
                  mb: 0.75,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                Claim amount
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.15,
                    color: amountAfter !== null ? "text.secondary" : "text.primary",
                  }}
                >
                  {formatUSD(amountNow)}
                </Typography>
                {amountAfter !== null && (
                  <>
                    <Typography variant="h5" color="text.disabled" sx={{ lineHeight: 1.15 }}>
                      →
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.15 }}
                    >
                      {formatUSD(amountAfter)}
                    </Typography>
                  </>
                )}
              </Box>
              {amountAfter !== null && (
                <Typography variant="body2" sx={{ color: "primary.main", fontStyle: "italic" }}>
                  +{formatUSD(adds)} from this file
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* What is on the claim, with this file's incoming lines on top, set apart. */}
        {loadFailed ? (
          <Typography variant="body2" color="error" data-testid="slc-preview-load-failed">
            {LOAD_FAILED_NOTE}
          </Typography>
        ) : (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
              {itemsHeading}
            </Typography>
            {itemsLoading ? (
              <Typography variant="body2" color="text.disabled">
                Loading claim items…
              </Typography>
            ) : (
              // Scrolls inside the dialog with the header pinned, so a claim with
              // dozens of items keeps its column names in view and never shows a
              // paginator inside a scroll box. Incoming rows sit at the top.
              <Box
                sx={{
                  maxHeight: "min(480px, 55vh)",
                  overflowY: "auto",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  "& .MuiPaper-root": { border: 0, borderRadius: 0, overflowX: "visible" },
                  "& .MuiPaper-root > div > div:first-of-type": { position: "sticky", top: 0, zIndex: 1 },
                }}
              >
                <CompactTable
                  data={rows}
                  columns={columns}
                  rowsPerPage={Math.max(rows.length, 1)}
                  emptyMessage="No claim items on this claim yet"
                  getRowSx={(l) =>
                    l.incoming
                      ? {
                          bgcolor: alpha(navy, 0.05),
                          boxShadow: `inset 3px 0 0 ${navy}`,
                          "& .MuiTypography-root": { fontStyle: "italic" },
                        }
                      : {}
                  }
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <Button
          variant="text"
          component={RouterLink}
          to={`/${ResourceName.STOPLOSS_CLAIMS}/${stoplossClaimId}/show`}
          target="_blank"
          rel="noopener"
          endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
        >
          Open claim
        </Button>
        <MUIButton variant={ButtonStyleType.Outlined} onClick={onClose}>
          Close
        </MUIButton>
      </DialogActions>
    </Dialog>
  );
};
